import { addDoc, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/services';
import type { ExtraDurationHours, RouteCategory, RouteDefinition } from '@/types';

export interface RouteFormValues {
  category: RouteCategory;
  extraDurationHours: ExtraDurationHours | null;
  name: string;
  distanceKm: number;
  payAmount: number;
  startTime: string;
  endTime: string;
  active: boolean;
  /** First calendar date (inclusive) this route can be claimed, `YYYY-MM-DD`. */
  activeFrom: string;
  /** Last calendar date (inclusive) this route can be claimed, `YYYY-MM-DD`, or `null`. */
  activeUntil: string | null;
}

const toRouteDocumentValues = (values: RouteFormValues) => {
  if (values.category === 'extra') {
    if (values.extraDurationHours === null) {
      throw new Error('Extra route requires a duration from 1 to 12 hours.');
    }
    return {
      ...values,
      name: `Extra ${values.extraDurationHours}h`,
      distanceKm: 0,
      payAmount: 0,
      startTime: '',
      endTime: '',
      activeFrom: '2000-01-01',
      activeUntil: null,
    };
  }
  return { ...values, extraDurationHours: null };
};

/** Creates a new route in the catalog. Only callable by admins (enforced by rules). */
export const createRoute = async (values: RouteFormValues, actorUid: string): Promise<void> => {
  const now = new Date().toISOString();
  await addDoc(collection(db, 'routes'), {
    ...toRouteDocumentValues(values),
    createdBy: actorUid,
    updatedBy: actorUid,
    createdAt: now,
    updatedAt: now,
  });
};

/** Updates an existing route's details. `createdBy` is immutable (enforced by rules). */
export const updateRoute = async (
  routeId: string,
  values: RouteFormValues,
  createdBy: string,
  actorUid: string,
): Promise<void> => {
  await updateDoc(doc(db, 'routes', routeId), {
    ...toRouteDocumentValues(values),
    createdBy,
    updatedBy: actorUid,
    updatedAt: new Date().toISOString(),
  } satisfies Partial<RouteDefinition>);
};

/**
 * Permanently removes a route from the catalog. Only callable by admins
 * (enforced by rules). Past assignments keep their own `routeSnapshot`, so
 * historical earnings/history views are unaffected; any *future*-dated
 * assignment slots still referencing this route are left as-is (routes are
 * retired via `active`/`activeUntil` in normal use, so this is expected to
 * only be used for routes that were never actually claimed).
 */
export const deleteRoute = async (routeId: string): Promise<void> => {
  await deleteDoc(doc(db, 'routes', routeId));
};
